import React, { useEffect, useState } from 'react';
import { Trash2, Plus, Save } from 'lucide-react';
import { useCategories } from '../context/CategoriesContext';
import { createCategory, updateCategory, deleteCategory, reorderCategories } from '../lib/api';
import { CHANNEL_ICONS, LAYOUTS, iconFor } from '../lib/icons';
import { ActionBtn, MoveButtons, moveItem } from '../components/ReorderControls';

const fieldCls = 'bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-body text-sm px-3 py-2.5 rounded';
const errText = (err) => err?.response?.data?.detail || err.message || 'Something went wrong';

export default function ChannelEditor() {
  const { categories, setCategories, reload } = useCategories();
  const [msg, setMsg] = useState(null);
  const [announce, setAnnounce] = useState('');

  const move = async (idx, dir) => {
    const next = moveItem(categories, idx, dir);
    setCategories(next);
    setAnnounce(`${categories[idx].name} moved to position ${idx + dir + 1} of ${next.length}`);
    try { setCategories(await reorderCategories(next.map((c) => c.key))); }
    catch (err) { setMsg({ type: 'err', text: errText(err) }); reload(); }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Remove the ${cat.name} channel? Its uploads are kept hidden and come back if you add a channel called "${cat.key.replace(/-/g, ' ')}" again.`)) return;
    try { await deleteCategory(cat.key); setMsg({ type: 'ok', text: `${cat.name} taken off air.` }); }
    catch (err) { setMsg({ type: 'err', text: errText(err) }); }
    reload();
  };

  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-8" data-testid="admin-channel-editor">
      <div className="space-y-3">
        <div className="sr-only" aria-live="polite">{announce}</div>
        <div className="font-osd text-[#f5a623] text-xs sm:text-sm tracking-widest">// CHANNEL LINEUP · HOME AND ABOUT ARE FIXED</div>
        {msg && (
          <div data-testid="channel-status"
               className={`font-osd text-xs px-3 py-2 rounded border ${msg.type === 'ok' ? 'text-[#9dd68a] border-[#9dd68a]/50 bg-[#0e1a09]' : 'text-[#ff6a3a] border-[#ff6a3a]/50 bg-[#1a0e05]'}`}>
            {msg.text}
          </div>
        )}
        {categories.length === 0 && (
          <div className="font-osd text-xs text-[#8b6a3e] border-2 border-dashed border-[#5a3418] px-4 py-8 text-center rounded">NO CHANNELS YET — ADD ONE.</div>
        )}
        {categories.map((cat, i) => (
          <ChannelRow key={cat.key} cat={cat} index={i} total={categories.length}
                      onMove={move} onRemove={remove} onSaved={reload} setMsg={setMsg} />
        ))}
      </div>
      <AddChannel onAdded={reload} setMsg={setMsg} />
    </div>
  );
}

function ChannelRow({ cat, index, total, onMove, onRemove, onSaved, setMsg }) {
  const [draft, setDraft] = useState(cat);
  useEffect(() => { setDraft(cat); }, [cat]);
  const dirty = ['name', 'layout', 'icon'].some((k) => draft[k] !== cat[k]);
  const Icon = iconFor(draft.icon);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    try {
      await updateCategory(cat.key, { name: draft.name, layout: draft.layout, icon: draft.icon });
      setMsg({ type: 'ok', text: 'Channel updated.' });
      onSaved();
    } catch (err) { setMsg({ type: 'err', text: errText(err) }); }
  };

  return (
    <div className="p-3 sm:p-4 border-2 border-[#3a2512] bg-[#0f0704] rounded-lg space-y-2.5" data-testid={`channel-row-${cat.key}`}>
      <div className="flex items-center gap-2">
        <span className="font-osd text-[11px] text-[#1a0e05] bg-[#f5a623] px-1.5 py-0.5 rounded-sm shrink-0">CH {String(index + 1).padStart(2, '0')}</span>
        <Icon className="w-5 h-5 text-[#f5a623] shrink-0" aria-hidden />
        <input value={draft.name} onChange={(e) => set('name', e.target.value)} aria-label="Channel name"
               className={`${fieldCls} flex-1 min-w-0`} data-testid={`channel-name-${cat.key}`} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select value={draft.layout} onChange={(e) => set('layout', e.target.value)} aria-label="Page style" className={fieldCls} data-testid={`channel-layout-${cat.key}`}>
          {LAYOUTS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
        </select>
        <select value={draft.icon} onChange={(e) => set('icon', e.target.value)} aria-label="Icon" className={fieldCls} data-testid={`channel-icon-${cat.key}`}>
          {Object.keys(CHANNEL_ICONS).map((k) => <option key={k} value={k}>Icon: {k}</option>)}
        </select>
      </div>
      <div className="font-osd text-[11px] text-[#8b6a3e] tracking-widest">URL: /{cat.key}</div>
      <div className="pt-2.5 border-t border-[#2a1608] flex items-center justify-between gap-2 flex-wrap">
        <MoveButtons index={index} total={total} onMove={onMove} label={cat.name}
                     upTestId={`channel-up-${cat.key}`} downTestId={`channel-down-${cat.key}`} />
        <div className="flex gap-1.5">
          {dirty && <ActionBtn testId={`channel-save-${cat.key}`} onClick={save} label={`Save ${cat.name}`} tone="accent"><Save className="w-4 h-4" /> SAVE</ActionBtn>}
          <ActionBtn testId={`channel-delete-${cat.key}`} onClick={() => onRemove(cat)} label={`Delete ${cat.name}`} tone="danger"><Trash2 className="w-4 h-4" /></ActionBtn>
        </div>
      </div>
    </div>
  );
}

function AddChannel({ onAdded, setMsg }) {
  const [form, setForm] = useState({ name: '', layout: 'mixed', icon: 'sparkles' });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const created = await createCategory(form);
      setMsg({ type: 'ok', text: `${created.name} is now on air at /${created.key}.` });
      setForm({ name: '', layout: 'mixed', icon: 'sparkles' });
      onAdded();
    } catch (err) { setMsg({ type: 'err', text: errText(err) }); }
  };

  return (
    <form onSubmit={submit} className="border-2 border-[#3a2512] bg-[#0f0704] p-4 sm:p-6 rounded-lg space-y-4 h-fit" data-testid="channel-add-form">
      <div className="font-osd text-[#f5a623] text-sm tracking-widest">// NEW CHANNEL</div>
      <label className="block">
        <span className="font-osd text-xs text-[#a88552] tracking-widest">NAME</span>
        <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Illustration" required
               className={`${fieldCls} mt-1 w-full`} data-testid="channel-add-name" />
      </label>
      <label className="block">
        <span className="font-osd text-xs text-[#a88552] tracking-widest">PAGE STYLE</span>
        <select value={form.layout} onChange={(e) => set('layout', e.target.value)} className={`${fieldCls} mt-1 w-full`} data-testid="channel-add-layout">
          {LAYOUTS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="font-osd text-xs text-[#a88552] tracking-widest">ICON</span>
        <select value={form.icon} onChange={(e) => set('icon', e.target.value)} className={`${fieldCls} mt-1 w-full`} data-testid="channel-add-icon">
          {Object.keys(CHANNEL_ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </label>
      <button type="submit" data-testid="channel-add-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#f5a623] text-[#1a0e05] font-osd tracking-widest px-5 py-3 rounded border-2 border-[#1a0e05] shadow-[3px_3px_0_#1a0e05] hover:bg-[#ffc968] transition-colors">
        <Plus className="w-4 h-4" /> ADD CHANNEL
      </button>
    </form>
  );
}
